import { useMemo } from "react";
import { motion } from "motion/react";
import type { EscalaLetter, EscalaScores } from "@/api/escala";

/**
 * Radar SVG das 6 letras ESCALA — versão polida (gradient fill + glow layer
 * no stroke + halo nos vértices + circle-grid em vez de polígono-grid +
 * entrada animada via framer-motion).
 *
 * Eixos seguem ordem E·S·C·A·L·A2 começando no topo, indo no sentido
 * horário. Score 0-85 (teto da metodologia v1.0).
 */

const LETTER_ORDER: EscalaLetter[] = ["E", "S", "C", "A", "L", "A2"];

const LETTER_DESC: Record<EscalaLetter, string> = {
  E: "Entender",
  S: "Sistematizar",
  C: "Captar",
  A: "Ativar",
  L: "Lucrar",
  A2: "Acelerar",
};

function letterChar(l: EscalaLetter): string {
  // A2 (Acelerar) renderiza como "A²" — Unicode superscript pra não confundir
  // com o A (Ativar). Funciona em SVG text e em texto comum.
  return l === "A2" ? "A²" : l;
}

export function EscalaRadar({
  scores,
  size = 320,
  strongest,
  weakest,
  showLabels = true,
  /** Teto da escala — default 100 mas no v1.0 só vai até 85 */
  maxValue = 100,
  /** Scores de um diagnóstico anterior — desenhados como polígono tracejado
   *  esmaecido por baixo, pra mostrar a evolução. */
  previousScores,
}: {
  scores: EscalaScores;
  size?: number;
  strongest?: EscalaLetter[];
  weakest?: EscalaLetter[];
  showLabels?: boolean;
  maxValue?: number;
  previousScores?: EscalaScores | null;
}) {
  const cx = size / 2;
  const cy = size / 2;
  const radius = size * 0.36;

  const angles = LETTER_ORDER.map(
    (_, i) => -Math.PI / 2 + (i * 2 * Math.PI) / 6,
  );

  const pointFor = (letter: EscalaLetter, scoreOverride?: number) => {
    const idx = LETTER_ORDER.indexOf(letter);
    const angle = angles[idx]!;
    const score = scoreOverride ?? scores[letter] ?? 0;
    const r = (Math.min(score, maxValue) / maxValue) * radius;
    return [cx + Math.cos(angle) * r, cy + Math.sin(angle) * r] as const;
  };

  const polyPoints = LETTER_ORDER.map((l) => {
    const [x, y] = pointFor(l);
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(" ");

  const prevPolyPoints = previousScores
    ? LETTER_ORDER.map((l) => {
        const idx = LETTER_ORDER.indexOf(l);
        const angle = angles[idx]!;
        const r = (Math.min(previousScores[l] ?? 0, maxValue) / maxValue) * radius;
        return `${(cx + Math.cos(angle) * r).toFixed(1)},${(cy + Math.sin(angle) * r).toFixed(1)}`;
      }).join(" ")
    : null;

  const gridRings = [25, 50, 75, 100];

  const labelPositions = LETTER_ORDER.map((l, i) => {
    const angle = angles[i]!;
    const r = radius + 28;
    return {
      letter: l,
      x: cx + Math.cos(angle) * r,
      y: cy + Math.sin(angle) * r,
      score: scores[l] ?? 0,
    };
  });

  // IDs únicos por instância pra defs SVG não colidirem entre múltiplos
  // radares na mesma página. Math.random é bullet-proof (sem dep de crypto).
  const uid = useMemo(() => Math.random().toString(36).slice(2, 10), []);
  const fillId = `esc-radar-fill-${uid}`;
  const glowId = `esc-radar-glow-${uid}`;

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      style={{ display: "block", margin: "0 auto", overflow: "visible" }}
    >
      <defs>
        <radialGradient id={fillId} cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#ff7000" stopOpacity="0.32" />
          <stop offset="70%" stopColor="#ff7000" stopOpacity="0.12" />
          <stop offset="100%" stopColor="#ff7000" stopOpacity="0.02" />
        </radialGradient>
        <filter id={glowId} x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="3" />
        </filter>
      </defs>

      {/* Grid: círculos concêntricos suaves (mais elegante que polígonos) */}
      {gridRings.map((pct) => (
        <circle
          key={pct}
          cx={cx}
          cy={cy}
          r={(pct / 100) * radius}
          fill="none"
          stroke="var(--wf-ink-5)"
          strokeWidth={0.8}
          strokeDasharray={pct === 100 ? "0" : "2 5"}
          opacity={pct === 100 ? 0.7 : 0.45}
        />
      ))}

      {/* Eixos super sutis */}
      {LETTER_ORDER.map((_, i) => {
        const angle = angles[i]!;
        return (
          <line
            key={i}
            x1={cx}
            y1={cy}
            x2={cx + Math.cos(angle) * radius}
            y2={cy + Math.sin(angle) * radius}
            stroke="var(--wf-ink-5)"
            strokeWidth={0.8}
            opacity={0.35}
          />
        );
      })}

      {/* Polígono anterior (tracejado discreto) */}
      {prevPolyPoints && (
        <polygon
          points={prevPolyPoints}
          fill="none"
          stroke="var(--wf-ink-3)"
          strokeWidth={1.2}
          strokeDasharray="3 3"
          strokeLinejoin="round"
          opacity={0.7}
        />
      )}

      {/* Glow externo do polígono (camada difusa pra dar profundidade) */}
      <motion.polygon
        points={polyPoints}
        fill="none"
        stroke="#ff7000"
        strokeWidth={8}
        strokeLinejoin="round"
        opacity={0.16}
        filter={`url(#${glowId})`}
        initial={{ opacity: 0, scale: 0.94 }}
        animate={{ opacity: 0.16, scale: 1 }}
        transition={{ duration: 0.8, delay: 0.1 }}
        style={{ transformOrigin: `${cx}px ${cy}px` }}
      />

      {/* Polígono principal com gradient fill + stroke nítido */}
      <motion.polygon
        points={polyPoints}
        fill={`url(#${fillId})`}
        stroke="var(--nd-orange)"
        strokeWidth={1.8}
        strokeLinejoin="round"
        strokeLinecap="round"
        initial={{ opacity: 0, scale: 0.94 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.75, ease: "easeOut" }}
        style={{ transformOrigin: `${cx}px ${cy}px` }}
      />

      {/* Vértices: halo difuso + dot nítido com borda branca */}
      {LETTER_ORDER.map((l, i) => {
        const [x, y] = pointFor(l);
        const isWeak = weakest?.includes(l);
        const isStrong = strongest?.includes(l);
        const baseColor = isWeak
          ? "var(--nd-orange-pun)"
          : isStrong
            ? "#3d7d3d"
            : "var(--nd-orange)";
        return (
          <g key={l}>
            <circle
              cx={x}
              cy={y}
              r={10}
              fill={baseColor}
              opacity={0.22}
              filter={`url(#${glowId})`}
            />
            <motion.circle
              cx={x}
              cy={y}
              r={5}
              fill={baseColor}
              stroke="#fff"
              strokeWidth={1.5}
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{
                delay: 0.35 + i * 0.06,
                duration: 0.4,
                ease: "backOut",
              }}
              style={{ transformOrigin: `${x}px ${y}px` }}
            />
          </g>
        );
      })}

      {/* Labels — letra grande, score abaixo, tipografia limpa */}
      {showLabels &&
        labelPositions.map((pos) => (
          <g key={pos.letter}>
            <text
              x={pos.x}
              y={pos.y - 6}
              textAnchor="middle"
              fontFamily="var(--wf-font-display)"
              fontSize={20}
              fontWeight={800}
              fill="var(--wf-ink)"
            >
              {letterChar(pos.letter)}
            </text>
            <text
              x={pos.x}
              y={pos.y + 16}
              textAnchor="middle"
              fontFamily="var(--wf-font-display)"
              fontSize={17}
              fontWeight={700}
              fill="var(--wf-ink)"
              style={{ fontVariantNumeric: "tabular-nums" }}
            >
              {pos.score}%
            </text>
          </g>
        ))}
    </svg>
  );
}

EscalaRadar.LETTER_ORDER = LETTER_ORDER;
EscalaRadar.LETTER_DESC = LETTER_DESC;
EscalaRadar.letterChar = letterChar;
